import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { queryKeys } from '@/lib/query-keys';
import { logDbOperation, LogCategory, startMeasure, endMeasure } from '@/utils/logger';
import { mapAssistantFromDb } from '../utils/dataMappers';

export interface DashboardData {
  assistants: any[];
  userPreferences: any;
  threadCount: number;
  timestamp: string;
}

/**
 * AI-focused dashboard query that fetches only AI-related data
 * Profile data is now handled by the dedicated useUserProfile hook
 */
export function useDashboardDataQuery(userId?: string) {
  return useQuery({
    queryKey: queryKeys.copilot.dashboardData(userId),
    queryFn: async (): Promise<DashboardData> => {
      if (!userId) throw new Error('User ID required');
      
      const startTime = performance.now();
      logDbOperation('SELECT', 'ai_dashboard_query', { userId });

      try {
        // Fetch AI-related data with correct table names and no problematic JOINs
        const [assistantsResult, preferencesResult, threadsCountResult] = await Promise.all([
          // Get all active assistants (no JOIN - fetch separately)
          supabase
            .from('agent_assistants')
            .select('*')
            .eq('is_active', true)
            .order('created_at', { ascending: false }),
          
          // Get user preferences separately
          supabase
            .from('agent_user_preferences')
            .select('*')
            .eq('user_id', userId)
            .single(),
          
          // Thread count with correct table name
          supabase
            .from('agent_threads')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', userId)
        ]);

        // Handle errors gracefully
        if (assistantsResult.error) {
          console.warn('[AI Dashboard] Assistants query error:', assistantsResult.error.message);
        }
        
        if (preferencesResult.error) {
          console.warn('[AI Dashboard] Preferences query error:', preferencesResult.error.message);
        }

        if (threadsCountResult.error) {
          console.warn('[AI Dashboard] Threads count error:', threadsCountResult.error.message);
        }



        // Process assistants data (bulk mapping instead of individual)
        const assistants = assistantsResult.data?.map((dbAssistant, index) => {
          // Bulk processing - only log summary instead of individual mappings
          if (index === 0) {
            console.log('🔄 Bulk processing assistants:', assistantsResult.data?.length);
          }
          return mapAssistantFromDb(dbAssistant, true); // Skip individual logs during bulk processing
        }) || [];
        
        // Get user preferences from the separate query
        const userPreferences = preferencesResult.data;

        const dashboardData: DashboardData = {
          assistants,
          userPreferences,
          threadCount: threadsCountResult.count || 0,
          timestamp: new Date().toISOString()
        };

        logDbOperation('SELECT_SUCCESS', 'ai_dashboard_query', { 
          assistantCount: assistants.length,
          hasPreferences: !!userPreferences,
          threadCount: dashboardData.threadCount
        });

        endMeasure(startTime, 'AI dashboard data fetch completed', LogCategory.DATABASE);
        
        const totalTime = Math.round(performance.now() - startTime);
        console.log('🤖 AI Dashboard Data:', {
          assistants: assistants.length,
          preferences: !!userPreferences,
          threads: dashboardData.threadCount,
          totalTime: `${totalTime}ms`
        });

        return dashboardData;

      } catch (error) {
        logDbOperation('ERROR', 'ai_dashboard_query', { 
          error: error instanceof Error ? error.message : 'Unknown error'
        });
        endMeasure(startTime, 'AI dashboard data fetch failed', LogCategory.DATABASE);
        throw error;
      }
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes - can be longer since no profile data
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    retryDelay: 1000,
    refetchOnWindowFocus: false,
  });
} 